import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertTriangle, RefreshCw, DollarSign, Users, Calendar, TrendingUp, Zap, Server, Phone, Cloud, FileText, Mail, Shield } from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';

interface ServiceItem {
  pk: number;
  account_id: number;
  api_id: string;
  status: string;
  provision_status: string;
  item_type: string;
  price: number;
  price_per_unit: number;
  quantity: number;
  date_created: string;
  activation_date: string | null;
  disconnect_date: string | null;
  prod_description: string;
  metadata: any;
  product: {
    pk: number;
    name: string;
    description: string;
    category: string;
    price: number;
    product_key: string;
    product_type: string;
  };
}

interface ServicesResponse {
  errors: boolean;
  message: string;
  result: ServiceItem[];
}

const Services: React.FC = () => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = authService.isAdmin();

  useEffect(() => {
    if (isAdmin) {
      loadServices();
    }
  }, [isAdmin]);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: ServicesResponse = await apiService.getServices();
      
      if (!response.errors && response.result) {
        setServices(response.result);
      } else {
        throw new Error(response.message || 'Failed to load services');
      }
    } catch (err: any) {
      console.error('Error loading services:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (itemType: string) => {
    const iconClass = "h-6 w-6";
    switch (itemType.toLowerCase()) {
      case 'itp_voice':
        return <Phone className={`${iconClass} text-blue-600`} />;
      case 'paylink360':
        return <DollarSign className={`${iconClass} text-green-600`} />;
      case 'itp_vfax_new_number':
        return <FileText className={`${iconClass} text-purple-600`} />;
      case 'itp_cloud':
        return <Cloud className={`${iconClass} text-sky-600`} />;
      case 'office365':
        return <Mail className={`${iconClass} text-orange-600`} />;
      case 'google_apps':
      case 'google_workspace':
        return <Cloud className={`${iconClass} text-green-600`} />;
      case 'it_managed_services':
        return <Shield className={`${iconClass} text-purple-600`} />;
      default:
        return <Server className={`${iconClass} text-gray-600`} />;
    }
  };

  const getStatusIcon = (status: string, provisionStatus: string) => {
    if (status === 'active' && provisionStatus === 'provisioned') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (provisionStatus === 'pending') {
      return <Clock className="h-5 w-5 text-yellow-500" />;
    } else if (status === 'inactive' || status === 'suspended') {
      return <AlertTriangle className="h-5 w-5 text-red-500" />;
    }
    return <Clock className="h-5 w-5 text-gray-500" />;
  };

  const getStatusText = (status: string, provisionStatus: string) => {
    if (status === 'active' && provisionStatus === 'provisioned') {
      return 'Active';
    } else if (provisionStatus === 'pending') {
      return 'Provisioning';
    } else if (status === 'inactive') {
      return 'Inactive';
    } else if (status === 'suspended') {
      return 'Suspended';
    }
    return 'Unknown';
  };

  const getStatusColor = (status: string, provisionStatus: string) => {
    if (status === 'active' && provisionStatus === 'provisioned') {
      return 'bg-green-100 text-green-800';
    } else if (provisionStatus === 'pending') {
      return 'bg-yellow-100 text-yellow-800';
    } else if (status === 'inactive' || status === 'suspended') {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Non-admins can't access this page
  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">You do not have permission to view this page. Only administrators can access the Services page.</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const activeServices = services.filter(s => s.status === 'active' && s.provision_status === 'provisioned').length;
  const pendingServices = services.filter(s => s.provision_status === 'pending').length;
  const inactiveServices = services.filter(s => s.status === 'inactive' || s.status === 'suspended').length;
  const totalMonthlyRevenue = services
    .filter(s => s.status === 'active')
    .reduce((sum, s) => sum + s.price, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error Loading Services</h3>
          <p className="text-red-600 mt-1">{error}</p>
          <button
            onClick={loadServices}
            className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors inline-flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Enterprise Services</h1>
            <p className="mt-2 text-lg text-gray-600">Comprehensive service management and insights</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={loadServices}
              className="inline-flex items-center px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <RefreshCw className="h-4 w-4 mr-2 text-gray-600" />
              <span className="text-gray-700 font-medium">Refresh</span>
            </button>
            <button className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg">
              <Zap className="h-4 w-4 mr-2" />
              <span className="font-medium">Manage Services</span>
            </button>
          </div>
        </div>
      </div>

      {/* Service Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-green-700">{activeServices}</p>
              <p className="text-green-600 font-medium mt-1">Active Services</p>
              <p className="text-green-500 text-sm mt-1">All systems operational</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-amber-700">{pendingServices}</p>
              <p className="text-amber-600 font-medium mt-1">Provisioning</p>
              <p className="text-amber-500 text-sm mt-1">Setup in progress</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-red-700">{inactiveServices}</p>
              <p className="text-red-600 font-medium mt-1">Issues</p>
              <p className="text-red-500 text-sm mt-1">Requires attention</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-blue-700">{formatCurrency(totalMonthlyRevenue)}</p>
              <p className="text-blue-600 font-medium mt-1">Monthly Cost</p>
              <p className="text-blue-500 text-sm mt-1 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                Total subscription value
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Services List */}
      {services.length > 0 ? (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Active Service Portfolio</h2>
            <p className="text-gray-600 mt-2">Complete overview of your enterprise services</p>
          </div>
          <div className="space-y-6">
            {services.map((service, index) => (
              <div 
                key={service.pk} 
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-6"
              >
                {/* Header Section */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                      {getServiceIcon(service.item_type)}
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{service.product.name}</h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {service.product.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 max-w-2xl">{service.prod_description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(service.status, service.provision_status)}`}>
                      {getStatusIcon(service.status, service.provision_status)}
                      <span className="ml-2">{getStatusText(service.status, service.provision_status)}</span>
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-700">Monthly Cost</p>
                        <p className="text-2xl font-bold text-green-900">{formatCurrency(service.price)}</p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg">
                        <DollarSign className="h-4 w-4 text-green-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-700">Quantity</p>
                        <p className="text-2xl font-bold text-blue-900">{service.quantity}</p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-700">Active Since</p>
                        <p className="text-lg font-bold text-purple-900">{formatDate(service.date_created)}</p>
                      </div>
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Calendar className="h-4 w-4 text-purple-600" />
                      </div>
                    </div>
                  </div>

                  {/* Dynamic 4th stat based on service type */}
                  {service.metadata?.purchased_users ? (
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-orange-700">Licensed Users</p>
                          <p className="text-2xl font-bold text-orange-900">{service.metadata.purchased_users}</p>
                        </div>
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <Users className="h-4 w-4 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  ) : service.metadata?.did ? (
                    <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-lg p-4 border border-cyan-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-cyan-700">DID Number</p>
                          <p className="text-lg font-bold text-cyan-900">{service.metadata.did}</p>
                        </div>
                        <div className="p-2 bg-cyan-100 rounded-lg">
                          <Phone className="h-4 w-4 text-cyan-600" />
                        </div>
                      </div>
                    </div>
                  ) : service.metadata?.price_per_did ? (
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-4 border border-emerald-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-700">Per DID Cost</p>
                          <p className="text-xl font-bold text-emerald-900">{formatCurrency(service.metadata.price_per_did)}</p>
                        </div>
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Phone className="h-4 w-4 text-emerald-600" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg p-4 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Service ID</p>
                          <p className="text-lg font-bold text-gray-900">#{service.pk}</p>
                        </div>
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Server className="h-4 w-4 text-gray-600" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-6xl mb-4">🔧</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
          <p className="text-gray-600">No services are currently configured for your account.</p>
        </div>
      )}

      {/* Service Categories Summary */}
      {services.length > 0 && (
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Service Categories Breakdown</h2>
            <p className="text-gray-600 mt-1">Revenue and utilization by service category</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['Voice', 'Hosted Service', 'Cloud'].map(category => {
              const categoryServices = services.filter(s => s.product.category === category);
              const categoryRevenue = categoryServices.reduce((sum, s) => sum + s.price, 0);
              const categoryIconMap = {
                'Voice': <Phone className="h-6 w-6 text-blue-600" />,
                'Hosted Service': <Server className="h-6 w-6 text-green-600" />,
                'Cloud': <Cloud className="h-6 w-6 text-purple-600" />
              };
              
              return categoryServices.length > 0 && (
                <div key={category} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      {categoryIconMap[category as keyof typeof categoryIconMap]}
                    </div>
                    <span className="text-2xl font-bold text-gray-900">{categoryServices.length}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{category}</h3>
                  <p className="text-sm text-gray-600 mb-3">{categoryServices.length} active service{categoryServices.length !== 1 ? 's' : ''}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-gray-900">{formatCurrency(categoryRevenue)}</span>
                    <span className="text-sm text-gray-500">/month</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;